import React, { useEffect, useState, useRef, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import layersConfig from "../config/layersConfig";
import basemapsConfig from "../config/basemapsConfig";
import NavigationBar from "./NavigationBar";
import { DragDropContext } from "react-beautiful-dnd";
import "mapbox-gl/dist/mapbox-gl.css";
import debounce from "lodash.debounce";
import { ImSpinner2 } from "react-icons/im";
import DeforestationComponent from "./DeforestationComponent";
import ForestLossRangeComponent from "./ForestLossRangeComponent";
import CanopyHeightComponent from "./CanopyHeightComponent";
import LoggingConcessionsComponent from "./LoggingConcessionsComponent";
import LandcoverComponent from "./LandcoverComponent";
import SarawakProtectedAreasComponent from "./SarawakProtectedAreasComponent";
import SarawakLPFComponent from "./SarawakLPFComponent";
import BiomassComponent from "./BiomassComponent";
import CarbonAnalysisPanel from "./CarbonAnalysisPanel";
import MapDrawController from "./MapDrawController";
import LayerAnalysisBox from "./LayerAnalysisBox";
import InfoBox from "./InfoBox"; // Import InfoBox component
import {
  SHARED_BOUNDARIES,
  SHARED_LABELS,
  SHARED_ROADS,
} from "../config/sharedOverlayLayers";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as turf from "@turf/turf";
import OilPalmConcessionsComponent from "./OilPalmConcessionsComponent";

const CustomAttribution = ({ text, link }) => {
  return (
    <div className="custom-attribution">
      <a href={link || "https://earthguard.io/"} target="_blank" rel="noopener noreferrer">
        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" className="attribution-logo" />
      </a>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://my-backend-api-80u5.onrender.com";
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const initialCenter = [112, 2];
const initialZoom = 5;

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const getDefaultDeforestationRange = () => {
  const today = new Date();
  const threeYearsAgo = new Date();
  threeYearsAgo.setFullYear(today.getFullYear() - 3);
  return {
    startDate: threeYearsAgo.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
};

const MapComponent = () => {
  const [showCarbonPanel, setShowCarbonPanel] = useState(false);
  const handleToggleCarbonPanel = () => setShowCarbonPanel((prev) => !prev);

  const [biomassYear, setBiomassYear] = useState(2020);

  const basemapRef = useRef(null);
  const layersMapRef = useRef(null);
  const [layersMapInstance, setLayersMapInstance] = useState(null);

  const isSyncingBasemapRef = useRef(false);
  const isSyncingLayersMapRef = useRef(false);

  const labelsMapRef = useRef(null);
  const boundariesMapRef = useRef(null);
  const roadsMapRef = useRef(null);
  const isSyncingBoundariesRef = useRef(false);
  const isSyncingLabelsRef = useRef(false);
  const isSyncingRoadsRef = useRef(false);

  const popupMapRef = useRef(null);
  const isSyncingPopupRef = useRef(false);

  const [activeLayers, setActiveLayers] = useState([]);
  const activeLayersRef = useRef(activeLayers);

  const [attributionText] = useState("");
  const [attributionLink] = useState("");

  const [currentBasemap, setCurrentBasemap] = useState(basemapsConfig[0]);
  const fetchControllerRef = useRef({});

  // Add state for selected features
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureType, setFeatureType] = useState(null);

  const [deforestationState, setDeforestationState] = useState({
    deforestationRange: getDefaultDeforestationRange(),
  });
  const debouncedUpdateDeforestationRange = useRef(
    debounce((newRange) => {
      setDeforestationState((prev) => ({ ...prev, deforestationRange: newRange }));
    }, 300)
  ).current;

  const handleDeforestationStateChange = useCallback(
    (newRange) => {
      console.log("Deforestation state updated:", newRange);
      debouncedUpdateDeforestationRange(newRange);
    },
    [debouncedUpdateDeforestationRange]
  );

  const [forestLossRange, setForestLossRange] = useState({ start: 2001, end: 2023 });
  const debouncedUpdateForestLossRange = useRef(
    debounce((newRange) => setForestLossRange(newRange), 300)
  ).current;

  const updateForestLossRangeHandler = useCallback(
    (newRange) => debouncedUpdateForestLossRange(newRange),
    [debouncedUpdateForestLossRange]
  );

  const toggleBiomassYear = useCallback((newYear) => {
    const map = layersMapRef.current;
    if (!map) return;

    const master = activeLayersRef.current.find((l) => l.id === "biomass-layer-id");
    if (!master || !master.subLayers) return;

    master.subLayers.forEach((sub) => {
      if (map.getLayer(sub.id)) {
        map.setLayoutProperty(sub.id, "visibility", "none");
      }
    });

    const targetSub = master.subLayers.find((s) => s.year === newYear);
    if (targetSub && map.getLayer(targetSub.id)) {
      map.setLayoutProperty(targetSub.id, "visibility", "visible");
    }

    setBiomassYear(newYear);
  }, []);

  const applyLayerFilter = useCallback(async (layerId, filterExpression) => {
    const targetLayer = activeLayersRef.current.find((l) => l.id === layerId);
    if (!targetLayer) return;

    addOrUpdateLayer({ ...targetLayer, loading: true });

    try {
      const layersMap = layersMapRef.current;
      if (layersMap && layersMap.getLayer(layerId)) {
        layersMap.setFilter(layerId, filterExpression);
      }
    } catch (err) {
      console.error("Error applying filter to layer:", err);
    } finally {
      const finalLayer = activeLayersRef.current.find((l) => l.id === layerId);
      if (finalLayer) addOrUpdateLayer({ ...finalLayer, loading: false });
    }
  }, []);

  const [yearRanges, setYearRanges] = useState({});
  useEffect(() => {
    activeLayersRef.current = activeLayers;
  }, [activeLayers]);

  const resetView = useCallback(() => {
    if (basemapRef.current) {
      basemapRef.current.flyTo({
        center: initialCenter,
        zoom: initialZoom,
        bearing: 0,
        pitch: 0,
        essential: true,
      });
    }
  }, []);

  const getOpacityProperty = (type) => {
    switch (type) {
      case "fill": return "fill-opacity";
      case "line": return "line-opacity";
      case "symbol": return "icon-opacity";
      case "raster": return "raster-opacity";
      default: return null;
    }
  };

  const handleOpacityChange = useCallback(
    (layerId, newOpacity) => {
      const layersMap = layersMapRef.current;
      if (!layersMap) return;

      const config = layersConfig.find((l) => l.id === layerId);
      if (!config) {
        console.error(`Layer ${layerId} not found in layersConfig`);
        return;
      }

      try {
        if (config.subLayers && config.subLayers.length > 0) {
          config.subLayers.forEach((sub) => {
            if (layersMap.getLayer(sub.id)) {
              const opacityProp = getOpacityProperty(sub.type);
              if (opacityProp) {
                layersMap.setPaintProperty(sub.id, opacityProp, newOpacity / 100);
              }
            }
          });
        } else {
          const opacityProp = getOpacityProperty(config.type);
          if (opacityProp && layersMap.getLayer(layerId)) {
            layersMap.setPaintProperty(layerId, opacityProp, newOpacity / 100);
          }
        }

        setActiveLayers((prev) =>
          prev.map((lyr) => (lyr.id === layerId ? { ...lyr, opacity: newOpacity } : lyr))
        );
      } catch (error) {
        console.error("Error setting opacity for", layerId, error);
      }
    },
    []
  );

  const addOrUpdateLayer = useCallback((layer) => {
    setActiveLayers((prev) => {
      const existingIndex = prev.findIndex((l) => l.id === layer.id);
      if (existingIndex !== -1) {
        const updated = { ...prev[existingIndex], ...layer };
        const newLayers = [...prev];
        newLayers[existingIndex] = updated;
        return newLayers;
      }
      return [...prev, layer];
    });
  }, []);

  useEffect(() => {
    const fixPointerEvents = () => {
      const basemapEl = document.getElementById('basemap');
      const layersMapEl = document.getElementById('layersMap');
      if (basemapEl) basemapEl.style.pointerEvents = 'auto';
      if (layersMapEl) layersMapEl.style.pointerEvents = 'auto';

      const nonInteractiveIds = ['boundariesMap', 'roadsMap', 'popupMap'];
      nonInteractiveIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.pointerEvents = 'none';
      });

      const labelsMapEl = document.getElementById('labelsMap');
      if (labelsMapEl) {
        const isInDrawingMode = document.querySelector('.mapbox-gl-draw_ctrl-draw-btn.active') !== null;
        if (!isInDrawingMode) labelsMapEl.style.pointerEvents = 'none';
      }
    };

    fixPointerEvents();
    const interval = setInterval(fixPointerEvents, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const initiallyActiveLayers = layersConfig
      .filter((layer) => layer.visibility === "visible")
      .map((layer) => ({
        ...layer,
        isVisible: layer.isVisible !== false,
        opacity: layer.opacity ?? 100,
        loading: false,
      }));

    setActiveLayers(initiallyActiveLayers);

    const initialYearRanges = {};
    initiallyActiveLayers.forEach((l) => {
      if (l.yearRange) initialYearRanges[l.id] = { ...l.yearRange };
    });
    setYearRanges(initialYearRanges);
  }, []);

  const replaceTilePlaceholders = useCallback((tileUrl, layer) => {
    if (layer.mapId) {
      return tileUrl
        .replace("{mapId}", layer.mapId)
        .replace("{yearStart}", layer.yearRange?.start || "")
        .replace("{yearEnd}", layer.yearRange?.end || "");
    }
    return tileUrl;
  }, []);

  const addSubLayer = useCallback(async (mapInstance, subLayer) => {
    const { id, type, source, getMapId, layout = {}, paint = {} } = subLayer;

    if (mapInstance.getLayer(id)) mapInstance.removeLayer(id);
    if (mapInstance.getSource(id)) mapInstance.removeSource(id);

    let sourceData = {};
    if (type === "raster") {
      let tiles = source?.tiles || [];
      if (typeof getMapId === "function") {
        try {
          const mapId = await getMapId();
          tiles = tiles.map((url) => url.replace("{mapId}", mapId));
        } catch (err) {
          console.error(`Error fetching mapId for subLayer ${id}:`, err);
          return;
        }
      }
      sourceData = { type: "raster", tiles, tileSize: 256 };
    } else if (["fill", "line", "symbol", "circle", "heatmap"].includes(type)) {
      if (!source?.data) {
        console.error(`Source data missing for subLayer ${id}`);
        return;
      }
      sourceData = { type: "geojson", data: source.data };
    } else {
      console.warn(`Unsupported layer type: ${type} for subLayer ${id}`);
      return;
    }

    mapInstance.addSource(id, sourceData);

    const opacityProp = getOpacityProperty(type);
    const finalPaint = { ...paint };
    if (opacityProp) finalPaint[opacityProp] = (subLayer.opacity ?? 100) / 100;

    mapInstance.addLayer({
      id,
      type,
      source: id,
      layout: { visibility: subLayer.isVisible ? "visible" : "none", ...layout },
      paint: finalPaint,
    });
  }, []);

  const addLayerToMap = useCallback(
    async (mapInstance, layer) => {
      try {
        if (layer.type === "custom") {
          addOrUpdateLayer({ ...layer, loading: false });
          return;
        }

        if (mapInstance.getLayer(layer.id)) mapInstance.removeLayer(layer.id);
        if (mapInstance.getSource(layer.id)) mapInstance.removeSource(layer.id);

        let tiles = layer.source?.tiles || [];
        if (layer.dynamicMapId && typeof layer.getMapId === "function") {
          const mapId = await layer.getMapId();
          if (!mapId) {
            console.error(`Failed to retrieve mapId for layer ${layer.id}`);
            return;
          }
          tiles = tiles.map((url) => url.replace("{mapId}", mapId));
        }

        if (layer.type === "geojson") {
          mapInstance.addSource(layer.id, { type: "geojson", data: layer.source.data });
          mapInstance.addLayer({
            id: layer.id,
            type: layer.geometryType || "fill",
            source: layer.id,
            layout: { visibility: layer.isVisible ? "visible" : "none" },
            paint: { "fill-color": layer.paint?.["fill-color"] || "#ffdb8f", "fill-opacity": (layer.opacity ?? 100) / 100 },
          });
        } else if (layer.type === "raster") {
          mapInstance.addSource(layer.id, { type: "raster", tiles: tiles.map((t) => replaceTilePlaceholders(t, layer)), tileSize: 256 });
          mapInstance.addLayer({
            id: layer.id,
            type: "raster",
            source: layer.id,
            paint: { "raster-opacity": (layer.opacity ?? 100) / 100 },
            layout: { visibility: layer.isVisible ? "visible" : "none" },
          });
        }

        if (layer.subLayers?.length > 0) {
          for (const sub of layer.subLayers) {
            await addSubLayer(mapInstance, sub);
          }
        }

        addOrUpdateLayer(layer);
      } catch (error) {
        if (error.name === "AbortError") {
          console.log(`Fetch aborted for layer ${layer.id}.`);
        } else {
          console.error(`Error adding layer ${layer.id}:`, error);
        }
      }
    },
    [addOrUpdateLayer, addSubLayer, replaceTilePlaceholders]
  );

  const syncLabelsMap = (sourceMap) => {
    if (!labelsMapRef.current || isSyncingLabelsRef.current) return;
    isSyncingLabelsRef.current = true;

    const labelMap = labelsMapRef.current;
    const center = sourceMap.getCenter();
    const zoom = sourceMap.getZoom();
    const bearing = sourceMap.getBearing();
    const pitch = sourceMap.getPitch();

    labelMap.setCenter(center);
    labelMap.setZoom(zoom);
    labelMap.setBearing(bearing);
    labelMap.setPitch(pitch);
    labelMap.triggerRepaint();

    isSyncingLabelsRef.current = false;
  };

  useEffect(() => {
    if (!mapboxgl.accessToken) {
      console.error("Mapbox access token not defined.");
      return;
    }

    const basemapInstance = new mapboxgl.Map({
      container: "basemap",
      style: currentBasemap.style,
      center: initialCenter,
      zoom: initialZoom,
      interactive: true,
    });
    basemapRef.current = basemapInstance;
    window.basemapInstance = basemapInstance;

    basemapInstance.on("load", () => {
      const overlays = currentBasemap.overlayLayers || {};
      [...(overlays.boundaries || []), ...(overlays.labels || []), ...(overlays.roads || [])].forEach(
        (layerId) => {
          if (basemapInstance.getLayer(layerId)) {
            basemapInstance.setLayoutProperty(layerId, "visibility", "none");
          }
        }
      );
    });

    basemapInstance.on("move", () => {
      if (isSyncingBasemapRef.current) return;
      syncLabelsMap(basemapInstance);
    });

    const layersMapInstanceLocal = new mapboxgl.Map({
      container: "layersMap",
      style: { version: 8, sources: {}, layers: [] },
      center: initialCenter,
      zoom: initialZoom,
      interactive: true,
      attributionControl: false,
    });
    layersMapRef.current = layersMapInstanceLocal;
    window.layersMapInstance = layersMapInstanceLocal;
    setLayersMapInstance(layersMapInstanceLocal);
    layersMapInstanceLocal.doubleClickZoom.disable();

    const labelsMap = new mapboxgl.Map({
      container: "labelsMap",
      style: currentBasemap.style,
      center: initialCenter,
      zoom: initialZoom,
      interactive: false,
      attributionControl: false,
      renderWorldCopies: true,
      antialias: true,
      localIdeographFontFamily: "'Noto Sans', 'Noto Sans CJK SC', sans-serif",
      maxBounds: basemapRef.current ? basemapRef.current.getMaxBounds() : undefined,
    });
    labelsMapRef.current = labelsMap;
    window.labelsMapInstance = labelsMap;

    const boundariesMap = new mapboxgl.Map({
      container: "boundariesMap",
      style: currentBasemap.style,
      center: initialCenter,
      zoom: initialZoom,
      interactive: false,
      attributionControl: false,
    });
    boundariesMapRef.current = boundariesMap;

    const roadsMap = new mapboxgl.Map({
      container: "roadsMap",
      style: currentBasemap.style,
      center: initialCenter,
      zoom: initialZoom,
      interactive: false,
      attributionControl: false,
    });
    roadsMapRef.current = roadsMap;

    const popupMap = new mapboxgl.Map({
      container: "popupMap",
      style: { version: 8, sources: {}, layers: [] },
      center: initialCenter,
      zoom: initialZoom,
      interactive: false,
      attributionControl: false,
    });
    popupMapRef.current = popupMap;

    function syncToAllOtherMaps(sourceMap, skipRefs = []) {
      const center = sourceMap.getCenter();
      const zoom = sourceMap.getZoom();
      const bearing = sourceMap.getBearing();
      const pitch = sourceMap.getPitch();

      [basemapRef, layersMapRef, labelsMapRef, boundariesMapRef, roadsMapRef, popupMapRef].forEach((mapRef) => {
        if (!mapRef.current || skipRefs.includes(mapRef)) return;

        const syncRef = getSyncRef(mapRef);
        if (syncRef) syncRef.current = true;

        mapRef.current.jumpTo({ center, zoom, bearing, pitch });

        if (syncRef) syncRef.current = false;
      });
    }

    const attachSyncHandlers = (mapRef, skipRef) => {
      const map = mapRef.current;
      if (!map) return;

      map.on("move", () => {
        const syncRef = getSyncRef(mapRef);
        if (syncRef.current) return;

        syncRef.current = true;
        syncToAllOtherMaps(map, [mapRef, skipRef]);
        syncRef.current = false;
      });
    };

    const getSyncRef = (mapRef) => {
      if (mapRef === basemapRef) return isSyncingBasemapRef;
      if (mapRef === layersMapRef) return isSyncingLayersMapRef;
      if (mapRef === labelsMapRef) return isSyncingLabelsRef;
      if (mapRef === boundariesMapRef) return isSyncingBoundariesRef;
      if (mapRef === roadsMapRef) return isSyncingRoadsRef;
      if (mapRef === popupMapRef) return isSyncingPopupRef;
      return null;
    };

    attachSyncHandlers(basemapRef);
    attachSyncHandlers(layersMapRef);
    attachSyncHandlers(labelsMapRef);
    attachSyncHandlers(boundariesMapRef);
    attachSyncHandlers(roadsMapRef);

    boundariesMap.on("load", () => {
      if (boundariesMap.getLayer("background")) {
        boundariesMap.setPaintProperty("background", "background-color", "rgba(0,0,0,0)");
      }
      const allLayers = boundariesMap.getStyle().layers || [];
      allLayers.forEach((lyr) => {
        if (!SHARED_BOUNDARIES.includes(lyr.id)) {
          boundariesMap.setLayoutProperty(lyr.id, "visibility", "none");
        }
      });
    });

    labelsMap.on("load", () => {
      if (labelsMap.getLayer("background")) {
        labelsMap.setPaintProperty("background", "background-color", "rgba(0,0,0,0)");
      }
      const allLayers = labelsMap.getStyle().layers || [];
      allLayers.forEach((lyr) => {
        if (!SHARED_LABELS.includes(lyr.id)) {
          labelsMap.setLayoutProperty(lyr.id, "visibility", "none");
        }
      });
    });

    roadsMap.on("load", () => {
      if (roadsMap.getLayer("background")) {
        roadsMap.setPaintProperty("background", "background-color", "rgba(0,0,0,0)");
      }
      const allLayers = roadsMap.getStyle().layers || [];
      allLayers.forEach((lyr) => {
        if (!SHARED_ROADS.includes(lyr.id)) {
          roadsMap.setLayoutProperty(lyr.id, "visibility", "none");
        }
      });
    });

    layersMapInstanceLocal.on("load", async () => {
      for (const layer of activeLayersRef.current) {
        addOrUpdateLayer({ ...layer, loading: true });
        try {
          await addLayerToMap(layersMapInstanceLocal, layer);
        } catch (err) {
          console.error("Error adding layer on load:", err);
        } finally {
          addOrUpdateLayer({ ...layer, loading: false });
        }
      }
    });

    return () => {
      basemapInstance.remove();
      layersMapInstanceLocal.remove();
      boundariesMap.remove();
      labelsMap.remove();
      roadsMap.remove();
      popupMap.remove();
      window.labelsMapInstance = null;
      window.layersMapInstance = null;
      console.log("Map instances removed.");
    };
  }, []);

// Updated double-click handler that uses InfoBox instead of popups
// In MapComponent.jsx, update your double-click handler:
// Look for the section that starts with "// Updated double-click handler that uses InfoBox instead of popups"

useEffect(() => {
  const layersMap = layersMapRef.current;
  if (!layersMap) {
    console.log("Layers map not available yet for double-click handler");
    return;
  }

  // Explicit mapping of fill layer IDs to their corresponding outline layer IDs.
  const outlineMapping = {
    "districts-fill-layer": "districts-layer",
    "divisions-fill-layer": "divisions-layer",
    "logging-concessions-a": "logging-concessions-a-outline",
    "sarawak-lpf-fill": "sarawak-lpf-outline",
    "oil-palm-concessions-fill": "oil-palm-concessions-outline"
  };

  const computeOutlineLayerId = (fillLayerId) =>
    outlineMapping[fillLayerId] || `${fillLayerId}-outline`;

  const handleDoubleClick = (e) => {
    console.log("Double-click event triggered at:", e.lngLat);

    // Use the fill layer IDs as the queryable layers.
    const queryLayers = [];
    if (layersMap.getLayer("districts-fill-layer")) queryLayers.push("districts-fill-layer");
    if (layersMap.getLayer("divisions-fill-layer")) queryLayers.push("divisions-fill-layer");
    if (layersMap.getLayer("logging-concessions-a")) queryLayers.push("logging-concessions-a");
    if (layersMap.getLayer("sarawak-lpf-fill")) queryLayers.push("sarawak-lpf-fill");
    if (layersMap.getLayer("oil-palm-concessions-fill")) queryLayers.push("oil-palm-concessions-fill");

    if (queryLayers.length === 0) {
      console.log("No queryable layers available");
      return;
    }

    const features = layersMap.queryRenderedFeatures(e.point, { layers: queryLayers });
    if (!features.length) {
      console.log("No features found at click point");
      return;
    }

    const feature = features[0];
    const fillLayerId = feature.layer.id;
    let featureName, featureType;

    // Determine feature name and type based on the fill layer id.
    switch (fillLayerId) {
      case "districts-fill-layer":
        featureName = feature.properties.name;
        featureType = "District";
        break;
      case "divisions-fill-layer":
        featureName = feature.properties.Division;
        featureType = "Division";
        break;
      case "logging-concessions-a":
        featureName = feature.properties.name || "Unnamed Concession";
        featureType = "Logging Concession";
        break;
      case "sarawak-lpf-fill":
        featureName = feature.properties.name || feature.properties.NAME || "Unnamed LPF";
        featureType = "License for Planted Forest";
        break;
      case "oil-palm-concessions-fill":
        featureName = feature.properties.name || feature.properties.NAME || "Unnamed Oil Palm Concession";
        featureType = "Oil Palm Concession";
        break;
      default:
        return;
    }

    // Determine the property field to use for matching. Default is "name".
    let nameField = "name";
    if (fillLayerId === "divisions-fill-layer") {
      nameField = "Division";
    } else if (fillLayerId === "sarawak-lpf-fill") {
      nameField = feature.properties.name ? "name" : "NAME";
    }

    const currentSelectedName = window[`selected${featureType.replace(/\s+/g, "")}Name`];
    const outlineLayerId = computeOutlineLayerId(fillLayerId);
    if (currentSelectedName === featureName) {
      // Deselect the feature.
      console.log(`Deselecting ${featureType}:`, featureName);
      window[`selected${featureType.replace(/\s+/g, "")}Name`] = null;
      layersMap.setPaintProperty(outlineLayerId, "line-color", "#000000");
      layersMap.setPaintProperty(outlineLayerId, "line-width", 0.5);
      setSelectedFeature(null);
      setFeatureType(null);
    } else {
      // Select the feature.
      console.log(`Selecting new ${featureType}:`, featureName);
      window[`selected${featureType.replace(/\s+/g, "")}Name`] = featureName;

      // Reset selections for all other feature types.
      const allLayerTypes = [
        "District",
        "Division",
        "LoggingConcession",
        "LicenseforPlantedForest",
        "OilPalmConcession",
      ];
      allLayerTypes.forEach((type) => {
        if (type !== featureType.replace(/\s+/g, "")) {
          window[`selected${type}Name`] = null;
          const otherFillId = {
            District: "districts-fill-layer",
            Division: "divisions-fill-layer",
            LoggingConcession: "logging-concessions-a",
            LicenseforPlantedForest: "sarawak-lpf-fill",
            OilPalmConcession: "oil-palm-concessions-fill"
          }[type];
          const otherOutlineId = computeOutlineLayerId(otherFillId);
          if (layersMap.getLayer(otherOutlineId)) {
            layersMap.setPaintProperty(otherOutlineId, "line-color", "#000000");
            layersMap.setPaintProperty(otherOutlineId, "line-width", 0.5);
          }
        }
      });

      // Use a data-driven expression to highlight the selected feature.
      const highlightExpression = [
        "case",
        ["==", ["get", nameField], featureName],
        featureType === "District"
          ? "#00FF00"
          : featureType === "Division"
            ? "#0dd2ef"
            : "#FF0000",
        "#000000"
      ];
      const widthExpression = [
        "case",
        ["==", ["get", nameField], featureName],
        4,
        0.5
      ];
      layersMap.setPaintProperty(outlineLayerId, "line-color", highlightExpression);
      layersMap.setPaintProperty(outlineLayerId, "line-width", widthExpression);

      const featureGeoJSON = {
        type: "Feature",
        properties: feature.properties,
        geometry: feature.geometry,
      };

      // Get the centroid for map navigation
      const centroid = turf.centroid(featureGeoJSON).geometry.coordinates;
      console.log("Calculated centroid:", centroid);

      // Use appropriate area field based on feature type
      let areaHa = null;

      // For districts, use Shape__Area field
      if (featureType === "District" && feature.properties.Shape__Area) {
        areaHa = parseFloat(feature.properties.Shape__Area) / 10000;
        console.log(`Using Shape__Area field for district: ${areaHa} hectares`);
      }
      // For oil palm concessions, use area_ha field
      else if (featureType === "Oil Palm Concession" && feature.properties.area_ha) {
        areaHa = parseFloat(feature.properties.area_ha);
        console.log(`Using area_ha field for oil palm: ${areaHa} hectares`);
      }
      // For Sarawak license/LPF, use area_offic field
      else if (featureType === "License for Planted Forest" && feature.properties.area_offic) {
        areaHa = parseFloat(feature.properties.area_offic);
        console.log(`Using area_offic field for license: ${areaHa} hectares`);
      }
      // General fallbacks
      else if (feature.properties.area_ha) {
        areaHa = parseFloat(feature.properties.area_ha);
        console.log(`Using area_ha property: ${areaHa} hectares`);
      }
      else if (feature.properties.area_offic) {
        areaHa = parseFloat(feature.properties.area_offic);
        console.log(`Using area_offic property: ${areaHa} hectares`);
      }
      else if (feature.properties.Shape__Area) {
        areaHa = parseFloat(feature.properties.Shape__Area) / 10000;
        console.log(`Using Shape__Area field: ${areaHa} hectares`);
      }
      else {
        // Last resort: calculate with turf
        const areaSqKm = turf.area(featureGeoJSON) / 1000000;
        areaHa = areaSqKm * 100;
        console.log(`Calculated area: ${areaHa} hectares (${areaSqKm} sq km)`);
      }

      // Update the state with selected feature
      const enhancedFeature = { ...feature, areaSqKm: areaHa / 100 };
      setSelectedFeature(enhancedFeature);
      setFeatureType(featureType);

      console.log(`Attempting flyTo for ${featureType} at:`, centroid);
      layersMap.flyTo({
        center: centroid,
        zoom: 8,
        speed: 1.2,
        curve: 1.4,
        essential: true,
      });
    }

    // Bring the fill and outline layers to the front.
    if (layersMap.getLayer(fillLayerId)) {
      layersMap.moveLayer(fillLayerId);
      if (layersMap.getLayer(outlineLayerId)) {
        layersMap.moveLayer(outlineLayerId);
      }
    }
  };

  layersMap.on("dblclick", handleDoubleClick);
  layersMap.on("moveend", () => {
    console.log("FlyTo completed, current center:", layersMap.getCenter(), "zoom:", layersMap.getZoom());
  });

  return () => {
    layersMap.off("dblclick", handleDoubleClick);
    layersMap.off("moveend");
  };
}, [layersMapRef, setSelectedFeature, setFeatureType]);

  // Add click handler to clear selection when clicking outside features
useEffect(() => {
  const layersMap = layersMapRef.current;
  if (!layersMap) return;

  const handleClick = (e) => {
    // Define queryable layers (add all layers you want to clear selection for)
    const queryLayers = [];
    if (layersMap.getLayer('districts-fill-layer')) queryLayers.push('districts-fill-layer');
    if (layersMap.getLayer('divisions-fill-layer')) queryLayers.push('divisions-fill-layer');
    if (layersMap.getLayer('logging-concessions-a')) queryLayers.push('logging-concessions-a');
    if (layersMap.getLayer('sarawak-lpf-fill')) queryLayers.push('sarawak-lpf-fill');
    // <-- Added oil palm fill layer to query
    if (layersMap.getLayer('oil-palm-concessions-fill')) queryLayers.push('oil-palm-concessions-fill');

    // Query for features at the click point
    const features = layersMap.queryRenderedFeatures(e.point, { layers: queryLayers });

    // If no features are found, clear selection for all types
    if (features.length === 0) {
      // Clear district selection (existing logic)
      if (window.selectedDistrictName) {
        window.selectedDistrictName = null;
        if (layersMap.getLayer('districts-layer')) {
          layersMap.setPaintProperty('districts-layer', 'line-color', '#000000');
          layersMap.setPaintProperty('districts-layer', 'line-width', 0.5);
        }
      }
      // Clear division selection (existing logic)
      if (window.selectedDivisionName) {
        window.selectedDivisionName = null;
        if (layersMap.getLayer('divisions-layer')) {
          layersMap.setPaintProperty('divisions-layer', 'line-color', '#000000');
          layersMap.setPaintProperty('divisions-layer', 'line-width', 0.5);
        }
      }
      // Clear logging concessions selection
      if (window.selectedLoggingConcessionName) {
        window.selectedLoggingConcessionName = null;
        if (layersMap.getLayer('logging-concessions-a-outline')) {
          layersMap.setPaintProperty('logging-concessions-a-outline', 'line-color', '#000000');
          layersMap.setPaintProperty('logging-concessions-a-outline', 'line-width', 0.5);
        }
      }
      // Clear license (LPF) selection
      if (window.selectedLicenseforPlantedForestName) {
        window.selectedLicenseforPlantedForestName = null;
        if (layersMap.getLayer('sarawak-lpf-outline')) {
          layersMap.setPaintProperty('sarawak-lpf-outline', 'line-color', '#000000');
          layersMap.setPaintProperty('sarawak-lpf-outline', 'line-width', 0.5);
        }
      }
      // Clear oil palm concession selection
      if (window.selectedOilPalmConcessionName) {
        window.selectedOilPalmConcessionName = null;
        if (layersMap.getLayer('oil-palm-concessions-outline')) {
          layersMap.setPaintProperty('oil-palm-concessions-outline', 'line-color', '#000000');
          layersMap.setPaintProperty('oil-palm-concessions-outline', 'line-width', 0.5);
        }
      }
      // Clear React state to hide the InfoBox
      setSelectedFeature(null);
      setFeatureType(null);
    }
  };

  layersMap.on('click', handleClick);

  return () => {
    layersMap.off('click', handleClick);
  };
}, [layersMapRef, selectedFeature, featureType, setSelectedFeature, setFeatureType]);

  const previousActiveLayers = usePrevious(activeLayers);

  useEffect(() => {
    const layersMap = layersMapRef.current;
    if (!layersMap || !layersMap.isStyleLoaded() || !previousActiveLayers) return;

    const prevIds = previousActiveLayers.map((l) => l.id);
    const currIds = activeLayers.map((l) => l.id);

    const addedIds = currIds.filter((id) => !prevIds.includes(id));
    const removedIds = prevIds.filter((id) => !currIds.includes(id));

    const newlyAddedLayers = activeLayers.filter((l) => addedIds.includes(l.id));
    newlyAddedLayers.forEach(async (lyr) => {
      addOrUpdateLayer({ ...lyr, loading: true });
      try {
        await addLayerToMap(layersMap, lyr);
      } catch (err) {
        console.error("Error adding newly active layer:", err);
      } finally {
        addOrUpdateLayer({ ...lyr, loading: false });
      }
    });

    activeLayers.forEach((layer) => {
      const prevLayer = previousActiveLayers.find((pl) => pl.id === layer.id);
      if (!prevLayer) return;

      if (prevLayer.opacity !== layer.opacity) {
        handleOpacityChange(layer.id, layer.opacity);
      }

      if (prevLayer.isVisible !== layer.isVisible) {
        if (layer.subLayers) {
          layer.subLayers.forEach((sub) => {
            if (layersMap.getLayer(sub.id)) {
              layersMap.setLayoutProperty(sub.id, "visibility", layer.isVisible ? "visible" : "none");
            }
          });
        } else if (layersMap.getLayer(layer.id)) {
          layersMap.setLayoutProperty(layer.id, "visibility", layer.isVisible ? "visible" : "none");
        }
      }

      if (layer.dynamicMapId && typeof layer.getMapId === "function" && layer.mapId !== prevLayer.mapId) {
        if (layersMap.getSource(layer.id)) {
          const source = layersMap.getSource(layer.id);
          if (source.setTiles) {
            source.setTiles(layer.source.tiles.map((tUrl) => replaceTilePlaceholders(tUrl, layer)));
            source.refresh?.();
          } else {
            if (layersMap.getLayer(layer.id)) layersMap.removeLayer(layer.id);
            layersMap.removeSource(layer.id);
            addLayerToMap(layersMap, layer);
          }
        }
      }
    });
  }, [activeLayers, addLayerToMap, addOrUpdateLayer, handleOpacityChange, previousActiveLayers, replaceTilePlaceholders]);

  useEffect(() => {
    const layersMap = layersMapRef.current;
    if (!layersMap) return;

    let popup = null;

    const showFeaturePopup = (e) => {
      const features = layersMap.queryRenderedFeatures(e.point, {
        layers: activeLayers.filter((l) => l.type === "geojson").map((l) => l.id),
      });
      if (!features.length) {
        hideFeaturePopup();
        return;
      }
      const feature = features[0];
      layersMap.getCanvas().style.cursor = "pointer";
      if (popup) popup.remove();

      popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
        .setLngLat(e.lngLat)
        .setHTML(`<div><strong>${feature.properties.name || "No Name"}</strong></div>`)
        .addTo(layersMap);
    };

    const hideFeaturePopup = () => {
      layersMap.getCanvas().style.cursor = "";
      if (popup) {
        popup.remove();
        popup = null;
      }
    };

    activeLayers.forEach((layer) => {
      if (layer.type === "geojson") {
        layersMap.on("mousemove", layer.id, showFeaturePopup);
        layersMap.on("mouseleave", layer.id, hideFeaturePopup);
      }
    });

    return () => {
      activeLayers.forEach((layer) => {
        if (layer.type === "geojson") {
          layersMap.off("mousemove", layer.id, showFeaturePopup);
          layersMap.off("mouseleave", layer.id, hideFeaturePopup);
        }
      });
      if (popup) popup.remove();
    };
  }, [activeLayers]);

  useEffect(() => {
    const layersMap = layersMapRef.current;
    if (!layersMap) return;

    [...activeLayers].reverse().forEach((layer) => {
      if (layer.subLayers && layer.subLayers.length > 0) {
        layer.subLayers.forEach((subLayer) => {
          if (layersMap.getLayer(subLayer.id)) layersMap.moveLayer(subLayer.id);
        });
      } else {
        if (layersMap.getLayer(layer.id)) layersMap.moveLayer(layer.id);
      }
    });
  }, [activeLayers]);

  const addLayer = useCallback(
    async (layer) => {
      const layersMap = layersMapRef.current;
      if (!layersMap) return;

      addOrUpdateLayer({ ...layer, isVisible: true, opacity: layer.opacity ?? 100, loading: true });

      try {
        await addLayerToMap(layersMap, layer);
      } catch (error) {
        console.error("Error adding layer:", error);
        addOrUpdateLayer({ ...layer, loading: false });
      }
    },
    [addLayerToMap, addOrUpdateLayer]
  );

  const handleToggleVisibility = useCallback(
    (layerId) => {
      const layer = activeLayersRef.current.find((l) => l.id === layerId);
      if (!layer) return;

      const map = layersMapRef.current;
      if (!map) return;

      if (layer.isVisible) {
        if (layerId === "deforestation-alerts-malaysia") {
          setDeforestationState({ deforestationRange: getDefaultDeforestationRange() });
          if (map.getLayer(layerId)) map.setFilter(layerId, null);
        }
        if (layerId === "forest-loss-range") {
          setForestLossRange({ start: 2001, end: 2023 });
          if (map.getLayer(layerId)) map.setFilter(layerId, null);
        }

        setActiveLayers((prev) =>
          prev.map((l) => (l.id === layerId ? { ...l, isVisible: false } : l))
        );
        map.setLayoutProperty(layerId, "visibility", "none");
      } else {
        const defaultLayer = layersConfig.find((l) => l.id === layerId);
        if (defaultLayer) addLayer(defaultLayer);
      }
    },
    [setDeforestationState, setForestLossRange, addLayer]
  );

  const removeLayer = useCallback(
    (layerId) => {
      if (layerId === "deforestation-alerts-malaysia") {
        setDeforestationState({ deforestationRange: getDefaultDeforestationRange() });
      }

      const layersMap = layersMapRef.current;
      if (!layersMap) return;

      setActiveLayers((prev) => prev.filter((l) => l.id !== layerId));

      const config = layersConfig.find((c) => c.id === layerId);
      if (!config) return;

      if (config.subLayers) {
        config.subLayers.forEach((sub) => {
          if (layersMap.getLayer(sub.id)) layersMap.removeLayer(sub.id);
          if (layersMap.getSource(sub.id)) layersMap.removeSource(sub.id);
        });
      } else {
        if (layersMap.getLayer(layerId)) layersMap.removeLayer(layerId);
        if (layersMap.getSource(layerId)) layersMap.removeSource(layerId);
      }

      if (fetchControllerRef.current[layerId]) {
        fetchControllerRef.current[layerId].abort();
        fetchControllerRef.current[layerId] = null;
      }
    },
    []
  );

  const changeBasemap = useCallback(
    async (basemapId) => {
      const newBasemap = basemapsConfig.find((b) => b.id === basemapId);
      if (!newBasemap || !basemapRef.current) return;

      const basemap = basemapRef.current;
      const layersMap = layersMapRef.current;
      const labelsMap = labelsMapRef.current;

      const viewState = {
        center: basemap.getCenter(),
        zoom: basemap.getZoom(),
        bearing: basemap.getBearing(),
        pitch: basemap.getPitch(),
      };

      let savedFeatures = [];
      try {
        if (labelsMap && labelsMap.__draw && typeof labelsMap.__draw.getAll === 'function') {
          savedFeatures = labelsMap.__draw.getAll().features;
          console.log("Saved drawn features:", savedFeatures);
          try {
            labelsMap.removeControl(labelsMap.__draw);
            console.log("Removed draw control before style change");
          } catch (err) {
            console.warn("Could not remove draw control:", err);
          }
        }
      } catch (err) {
        console.warn("Error saving drawn features:", err);
        savedFeatures = [];
      }

      basemap.setStyle(newBasemap.style);

      basemap.once("style.load", () => {
        basemap.jumpTo({
          center: viewState.center,
          zoom: viewState.zoom,
          bearing: viewState.bearing,
          pitch: viewState.pitch,
        });

        const overlays = newBasemap.overlayLayers || {};
        [...(overlays.boundaries || []), ...(overlays.labels || []), ...(overlays.roads || [])].forEach(
          (layerId) => {
            if (basemap.getLayer(layerId)) {
              basemap.setLayoutProperty(layerId, "visibility", "none");
            }
          }
        );

        const updateOverlayMaps = async () => {
          if (labelsMap) {
            await labelsMap.setStyle(newBasemap.style);
            labelsMap.once("style.load", () => {
              labelsMap.jumpTo(viewState);
              if (labelsMap.getLayer("background")) {
                labelsMap.setPaintProperty("background", "background-color", "rgba(0,0,0,0)");
              }
              const allLayers = labelsMap.getStyle().layers || [];
              allLayers.forEach((lyr) => {
                if (!SHARED_LABELS.includes(lyr.id)) {
                  labelsMap.setLayoutProperty(lyr.id, "visibility", "none");
                }
              });

              if (savedFeatures.length > 0) {
                try {
                  const newDrawControl = new MapboxDraw({
                    displayControlsDefault: false,
                    controls: { polygon: true, trash: true },
                    styles: [
                      { id: 'gl-draw-polygon-fill', type: 'fill', filter: ['all', ['==', '$type', 'Polygon']], paint: { 'fill-color': '#3b99fc', 'fill-outline-color': '#3b99fc', 'fill-opacity': 0.3 } },
                      { id: 'gl-draw-polygon-stroke-active', type: 'line', filter: ['all', ['==', '$type', 'Polygon']], paint: { 'line-color': '#3b99fc', 'line-width': 3 } },
                      { id: 'gl-draw-polygon-and-line-vertex-active', type: 'circle', filter: ['all', ['==', 'meta', 'vertex']], paint: { 'circle-radius': 5, 'circle-color': '#fff', 'circle-stroke-color': '#3b99fc', 'circle-stroke-width': 2 } }
                    ]
                  });

                  labelsMap.addControl(newDrawControl);
                  labelsMap.__draw = newDrawControl;
                  if (layersMapRef.current) layersMapRef.current.__draw = newDrawControl;
                  if (window.labelsMapInstance) window.labelsMapInstance.__draw = newDrawControl;

                  labelsMap.on("draw.create", (e) => {
                    const polygon = e.features[0];
                    const areaSqMeters = turf.area(polygon);
                    const areaHectares = areaSqMeters / 10000;
                    if (window.updatePolygonArea) window.updatePolygonArea(areaHectares);
                  });

                  labelsMap.on("draw.update", (e) => {
                    const polygon = e.features[0];
                    const areaSqMeters = turf.area(polygon);
                    const areaHectares = areaSqMeters / 10000;
                    if (window.updatePolygonArea) window.updatePolygonArea(areaHectares);
                  });

                  labelsMap.on("draw.delete", () => {
                    if (window.updatePolygonArea) window.updatePolygonArea(0);
                  });

                  setTimeout(() => {
                    if (savedFeatures.length > 0 && newDrawControl && typeof newDrawControl.add === 'function') {
                      newDrawControl.add({ type: 'FeatureCollection', features: savedFeatures });
                      console.log("Restored draw features with new control");
                    }
                  }, 200);

                  const hideMapboxControls = () => {
                    const controlSelectors = [
                      ".mapbox-gl-draw_ctrl",
                      ".mapboxgl-ctrl-top-right",
                      ".mapboxgl-ctrl-top-left",
                      ".mapbox-gl-draw_point",
                      ".mapbox-gl-draw_polygon",
                      ".mapbox-gl-draw_trash",
                    ];

                    controlSelectors.forEach(selector => {
                      document.querySelectorAll(selector).forEach(element => {
                        if (element) {
                          element.style.display = "none";
                          element.style.visibility = "hidden";
                          element.style.opacity = "0";
                          element.style.pointerEvents = "none";
                        }
                      });
                    });

                    document.querySelectorAll(".mapboxgl-ctrl-group").forEach(control => {
                      if (control && control.querySelector(".mapbox-gl-draw_point, .mapbox-gl-draw_polygon, .mapbox-gl-draw_trash")) {
                        control.style.display = "none";
                        control.style.visibility = "hidden";
                      }
                    });
                  };

                  hideMapboxControls();
                  const hideInterval = setInterval(hideMapboxControls, 300);
                  setTimeout(() => clearInterval(hideInterval), 2000);
                } catch (err) {
                  console.error("Error creating new draw control:", err);
                }
              }

              labelsMap.triggerRepaint();
            });
          }

          if (boundariesMapRef.current) {
            const boundariesMap = boundariesMapRef.current;
            try {
              await boundariesMap.setStyle(newBasemap.style);
              boundariesMap.once("style.load", () => {
                boundariesMap.jumpTo(viewState);
                if (boundariesMap.getLayer("background")) {
                  boundariesMap.setPaintProperty("background", "background-color", "rgba(0,0,0,0)");
                }
                const allLayers = boundariesMap.getStyle().layers || [];
                allLayers.forEach((lyr) => {
                  if (!SHARED_BOUNDARIES.includes(lyr.id)) {
                    boundariesMap.setLayoutProperty(lyr.id, "visibility", "none");
                  }
                });
              });
            } catch (err) {
              console.warn("Error updating boundaries map:", err);
            }
          }

          if (roadsMapRef.current) {
            const roadsMap = roadsMapRef.current;
            try {
              await roadsMap.setStyle(newBasemap.style);
              roadsMap.once("style.load", () => {
                roadsMap.jumpTo(viewState);
                if (roadsMap.getLayer("background")) {
                  roadsMap.setPaintProperty("background", "background-color", "rgba(0,0,0,0)");
                }
                const allLayers = roadsMap.getStyle().layers || [];
                allLayers.forEach((lyr) => {
                  if (!SHARED_ROADS.includes(lyr.id)) {
                    roadsMap.setLayoutProperty(lyr.id, "visibility", "none");
                  }
                });
              });
            } catch (err) {
              console.warn("Error updating roads map:", err);
            }
          }
        };

        if (layersMap && layersMap.isStyleLoaded()) {
          const currentActiveLayers = [...activeLayersRef.current];
          currentActiveLayers.forEach(async (layer) => {
            addOrUpdateLayer({ ...layer, loading: true });
            try {
              await addLayerToMap(layersMap, layer);
            } catch (err) {
              console.error("Error re-adding layer after basemap change:", err);
            } finally {
              addOrUpdateLayer({ ...layer, loading: false });
            }
          });
        }

        updateOverlayMaps();
      });

      setCurrentBasemap(newBasemap);
    },
    [basemapsConfig, addLayerToMap, addOrUpdateLayer]
  );

  useEffect(() => {
    return () => {
      Object.keys(fetchControllerRef.current).forEach((key) => {
        if (fetchControllerRef.current[key]) fetchControllerRef.current[key].abort();
      });
      debouncedUpdateForestLossRange.cancel();
      debouncedUpdateDeforestationRange.cancel();
    };
  }, [debouncedUpdateForestLossRange, debouncedUpdateDeforestationRange]);

  const onDragEnd = useCallback(
    (result) => {
      const { destination, source } = result;
      if (!destination) return;
      if (destination.droppableId === source.droppableId && destination.index === source.index) return;

      const reordered = Array.from(activeLayers);
      const [movedLayer] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, movedLayer);
      setActiveLayers(reordered);
    },
    [activeLayers]
  );

  const resetLayers = useCallback(() => {
    setDeforestationState({ deforestationRange: getDefaultDeforestationRange() });
    debouncedUpdateForestLossRange.cancel();

    Object.keys(fetchControllerRef.current).forEach((layerId) => {
      if (fetchControllerRef.current[layerId]) {
        fetchControllerRef.current[layerId].abort();
        fetchControllerRef.current[layerId] = null;
      }
    });

    activeLayers.forEach((layer) => {
      if (layer.id === "biomass-layer-id") {
        setActiveLayers((prevLayers) =>
          prevLayers.map((l) => (l.id === "biomass-layer-id" ? { ...l, isVisible: true } : l))
        );
        if (layer.subLayers && layer.subLayers.length > 0) {
          const defaultYear = layer.subLayers[0].year;
          toggleBiomassYear(defaultYear);
          console.log("Biomass layer reset to default year", defaultYear);
        }
      } else {
        removeLayer(layer.id);
      }
    });

    setYearRanges({});
    changeBasemap(basemapsConfig[0].id);
    setForestLossRange({ start: 2001, end: 2023 });

    const defaultLayers = layersConfig
      .filter((l) => l.visibility === "visible" && l.id !== "biomass-layer-id")
      .map((l) => ({ ...l, isVisible: true, opacity: l.opacity ?? 100, loading: false }));
    setActiveLayers((prevLayers) => [...prevLayers, ...defaultLayers]);

    const initRanges = {};
    defaultLayers.forEach((l) => {
      if (l.yearRange) initRanges[l.id] = { ...l.yearRange };
    });
    setYearRanges(initRanges);
    updateForestLossRangeHandler({ start: 2001, end: 2023 });
  }, [
    activeLayers,
    changeBasemap,
    debouncedUpdateForestLossRange,
    removeLayer,
    updateForestLossRangeHandler,
    toggleBiomassYear,
  ]);

  const layerAnalysisBoxRef = useRef(null);
  const handleAnalyzePolygon = useCallback(() => {
    if (layerAnalysisBoxRef.current) layerAnalysisBoxRef.current.startAnalysis();
  }, []);

  // Feature selection handler for BasemapToggle
  const handleFeatureSelect = useCallback((feature, type) => {
    setSelectedFeature(feature);
    setFeatureType(type);
  }, []);

  return (
    <div id="map" style={{ display: "flex", height: "100vh", position: "relative" }}>
      <div id="basemap" style={{ position: "absolute", top: 0, bottom: 0, width: "100%", zIndex: 1 }} />
      <div id="layersMap" style={{ position: "absolute", top: 0, bottom: 0, width: "100%", pointerEvents: "auto", zIndex: 5 }} />
      <div id="boundariesMap" style={{ position: "absolute", top: 0, bottom: 0, width: "100%", pointerEvents: "none", zIndex: 3 }} />
      <div id="roadsMap" style={{ position: "absolute", top: 0, bottom: 0, width: "100%", pointerEvents: "none", zIndex: 4 }} />
      <div id="labelsMap" style={{ position: "absolute", top: 0, bottom: 0, width: "100%", pointerEvents: "none", zIndex: 6, willChange: "transform" }} />
      <div id="popupMap" style={{ position: "absolute", top: 0, bottom: 0, width: "100%", pointerEvents: "none", zIndex: 8 }} />
      <div id="mapboxgl-popup-container" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 10 }} />



      <CustomAttribution text={attributionText} link={attributionLink} />

      <style>
        {`
          .mapboxgl-canvas { z-index: 1 !important; }
          #layersMap { z-index: 7 !important; }
          #layersMap canvas { z-index: 1 !important; opacity: 1 !important; visibility: visible !important; }
          .mapboxgl-popup { z-index: 999 !important; pointer-events: auto !important; }
          .mapboxgl-popup-content { background: white !important; padding: 10px !important; border-radius: 4px !important; box-shadow: 0 1px 4px rgba(0,0,0,0.2) !important; z-index: 1000 !important; }
          .mapboxgl-popup-tip { z-index: 999 !important; }
          .sarawak-protected-areas-popup, .logging-concession-popup, .sarawak-lpf-popup { z-index: 999 !important; }
          .mapboxgl-map .mapboxgl-popup { position: absolute !important; z-index: 999 !important; }
          .mapboxgl-map .mapboxgl-popup-content, .mapboxgl-map .mapboxgl-popup-tip { position: relative !important; z-index: 1000 !important; }
          .custom-attribution { position: absolute; bottom: 0; left: 0; margin-left: 100px; padding: 0 5px; border-radius: 2px; font-size: 10px; line-height: 18px; z-index: 7; display: flex; align-items: center; }
          .custom-attribution a { color: rgba(0, 0, 0, 0.75); text-decoration: none; display: flex; align-items: center; gap: 4px; }
          .custom-attribution a:hover { text-decoration: underline; }
          .attribution-logo { height: 20px; width: auto; vertical-align: middle; background-color: transparent; padding: 0; bottom: 13px; position: absolute; left: 20px; filter: drop-shadow(0px 0px 1px black); }
        `}
      </style>

      <MapDrawController map={layersMapInstance}>
        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "DeforestationComponent" && layer.isVisible) && (
          <DeforestationComponent
            layerConfig={activeLayers.find((l) => l.id === "deforestation-alerts-malaysia")}
            mapInstance={layersMapRef.current}
            id="deforestation-alerts-malaysia"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "deforestation-alerts-malaysia")?.opacity ?? 100}
            dateRange={deforestationState.deforestationRange}
            onLoad={(updatedConfig) => addOrUpdateLayer({ ...updatedConfig, loading: false })}
          />
        )}

        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "ForestLossRangeComponent" && layer.isVisible) && (
          <ForestLossRangeComponent
            layerConfig={activeLayers.find((l) => l.id === "forest-loss-range")}
            mapInstance={layersMapRef.current}
            yearRange={forestLossRange}
            onLoad={(updatedConfig) => addOrUpdateLayer({ ...updatedConfig, loading: false })}
          />
        )}

        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "BiomassComponent" && layer.isVisible) && (
          <BiomassComponent
            layerConfig={activeLayers.find((l) => l.id === "biomass-layer-id")}
            mapInstance={layersMapRef.current}
            id="biomass-layer-id"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "biomass-layer-id")?.opacity ?? 100}
          />
        )}

        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "CanopyHeightComponent" && layer.isVisible) && (
          <CanopyHeightComponent
            layerConfig={activeLayers.find((l) => l.id === "canopy-height")}
            mapInstance={layersMapRef.current}
            id="canopy-height"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "canopy-height")?.opacity ?? 100}
            onLoad={(updatedConfig) => addOrUpdateLayer({ ...updatedConfig, loading: false })}
          />
        )}

        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "LoggingConcessionsComponent" && layer.isVisible) && (
          <LoggingConcessionsComponent
            layerConfig={activeLayers.find((l) => l.id === "logging-concessions")}
            mapInstance={layersMapRef.current}
            popupMapInstance={popupMapRef.current}
            id="logging-concessions"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "logging-concessions")?.opacity ?? 50}
          />
        )}

        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "SarawakProtectedAreasComponent" && layer.isVisible) && (
          <SarawakProtectedAreasComponent
            layerConfig={activeLayers.find((l) => l.id === "sarawak_protected_areas")}
            mapInstance={layersMapRef.current}
            popupMapInstance={popupMapRef.current}
            id="sarawak_protected_areas"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "sarawak_protected_areas")?.opacity ?? 50}
          />
        )}

        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "SarawakLPFComponent" && layer.isVisible) && (
          <SarawakLPFComponent
            layerConfig={activeLayers.find((l) => l.id === "sarawak-lpf")}
            mapInstance={layersMapRef.current}
            popupMapInstance={popupMapRef.current}
            id="sarawak-lpf"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "sarawak-lpf")?.opacity ?? 50}
          />
        )}

        {activeLayers.find(
          (layer) =>
            layer.type === "custom" &&
            layer.component === "OilPalmConcessionsComponent" &&
            layer.isVisible
        ) && (
          <OilPalmConcessionsComponent
            layerConfig={activeLayers.find((l) => l.id === "oil-palm-concessions")}
            mapInstance={layersMapRef.current}
            popupMapInstance={popupMapRef.current}
            id="oil-palm-concessions"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "oil-palm-concessions")?.opacity ?? 50}
          />
        )}


        {activeLayers.find((layer) => layer.type === "custom" && layer.component === "LandcoverComponent" && layer.isVisible) && (
          <LandcoverComponent
            layerConfig={activeLayers.find((l) => l.id === "landcover2020")}
            mapInstance={layersMapRef.current}
            id="landcover2020"
            isVisible={true}
            opacity={activeLayers.find((l) => l.id === "landcover2020")?.opacity ?? 100}
          />
        )}

        <DragDropContext onDragEnd={onDragEnd}>
          <NavigationBar
            map={basemapRef.current}
            activeLayers={activeLayers}
            addLayer={addLayer}
            removeLayer={removeLayer}
            resetLayers={resetLayers}
            basemapsConfig={basemapsConfig}
            labelsMapRef={labelsMapRef}
            boundariesMapRef={boundariesMapRef}
            roadsMapRef={roadsMapRef}
            layersMapRef={layersMapRef}
            currentBasemap={currentBasemap}
            changeBasemap={changeBasemap}
            forestLossRange={forestLossRange}
            updateForestLossRange={updateForestLossRangeHandler}
            deforestationRange={deforestationState.deforestationRange}
            updateDeforestationRange={debouncedUpdateDeforestationRange}
            onToggleVisibility={handleToggleVisibility}
            onOpacityChange={handleOpacityChange}
            resetView={resetView}
            layersConfig={layersConfig}
            applyLayerFilter={applyLayerFilter}
            biomassYear={biomassYear}
            updateBiomassYear={toggleBiomassYear}
            onToggleCarbonPanel={handleToggleCarbonPanel}
            carbonPanelVisible={showCarbonPanel}
              selectedFeature={selectedFeature}
  selectedFeatureType={featureType}
            onFeatureSelect={handleFeatureSelect}
          />
        </DragDropContext>

        {showCarbonPanel && layersMapInstance && (
          <CarbonAnalysisPanel onAnalyzePolygon={handleAnalyzePolygon} map={layersMapInstance} />
        )}

        {layersMapInstance && <LayerAnalysisBox ref={layerAnalysisBoxRef} map={layersMapInstance} />}
      </MapDrawController>
    </div>
  );
};

export default MapComponent;
